import LocationPageAlertType from '@alltrails/amplitude/enums/LocationPageAlertType';
import type TrailLength from '@alltrails/core/types/TrailLength';
import Access from '@alltrails/filters/constants/accessEnum';
import { FilterConfig as SharedFilterConfig } from '@alltrails/filters/types/filters';
import type { ShareObject } from '@alltrails/sharing/types/ShareObject';
import { AlgoliaTrail, TrailDifficulty } from '@alltrails/trails';
import type { ActivityType } from '@alltrails/types/ActivityType';
import type { GuideHit, POISubType } from '@alltrails/types/AlgoliaResults';
import type { Review } from '@alltrails/types/Review';
import type TrailFeature from '@alltrails/types/TrailFeature';
import { LocationBreadcrumb, ParkRanking, PhotoHash, River } from '../shared';

export type LocationDetailsPageProps = {
  details: LocationDetailsApiResponse;
  isBot: boolean;
};

export type ReviewAndTrail = {
  review_snippet: Review;
  trail: AlgoliaTrail;
};

export type FAQTrail = {
  name: string;
  duration: string | null;
  length: number | string;
  elevationGain: number;
  starRating: number | string;
  reviewCount: number;
  slug: string;
};

export type FAQParkAggregatesType = {
  totalTrails: number;
  averageRating: number;
  reviewCount: number;
};

export type FAQTrailType = {
  name: string;
  count: number;
};

export type FAQNationalParkType = {
  name: string;
  slug: string;
  trailCount: number;
  reviewCount: number;
  averageRating: number;
};

export type ActivityFaq = {
  count: number;
  mostPopular: FAQTrail;
};

export type FaqDataType = {
  longestTrail: FAQTrail | null;
  bestTrail: FAQTrail | null;
  mostElevationGainTrails: FAQTrail[] | null;
  popularDifficultTrail: FAQTrail | null;
  faqTrailTypes: FAQTrailType[] | null;
  faqNationalParks?: FAQNationalParkType[] | null;
  faqParkAggregates?: FAQParkAggregatesType | null;
  campingTrails: ActivityFaq | null;
  runningTrails?: ActivityFaq | null;
  bikingTrails?: ActivityFaq | null;
  northernSeasonalTrails?: {
    summer: FAQTrail[];
    winter: FAQTrail[];
    fall: FAQTrail[];
    spring: FAQTrail[];
  } | null;
  poiTypes?: {
    [key: string]: {
      trailsCount: number;
      trails: FAQTrail[];
    };
  } | null;
};

export type LocationDetailsResponseType = 'poi' | 'park' | 'city' | 'state' | 'country';

export type FilterConfig = {
  baseFilters: SharedFilterConfig;
  enabledFilters: string[];
};

export type ParkInfoType = {
  ranking: ParkRanking | null;
  hasVisitorInfo?: boolean;
};

export type Tag = ActivityType | TrailFeature | Access | TrailDifficulty;
export type PageFilterType = ActivityType | TrailFeature | TrailDifficulty | TrailLength;

export type SeoLinksSectionKey = 'REGIONS' | 'STATES' | 'TRAILS' | 'PARKS' | 'CITIES';

export type SeoLinks = {
  url: string;
  title: string;
}[];

export type VisitorStats = {
  visitorsCount: string;
  busiestMonth: string;
};

export type EasiestTrailsToGetToPoiType = {
  shortest: AlgoliaTrail;
  leastElevationGain: AlgoliaTrail;
  topKidFriendly: AlgoliaTrail | null;
  topPavedOrPartiallyPaved: AlgoliaTrail | null;
};

export type PointOfInterest = {
  name: string;
  slug: string;
  id: number;
  type?: POISubType;
  typeLabel?: string;
};

export type LocationBasePageApiResponse = {
  id: number;
  name: string;
  cityName?: string | null;
  stateName: string | null;
  countryName: string;
  locationSlug: string;
  geo: {
    latitude: string;
    longitude: string;
  };
  shareObject: ShareObject;
  rating: {
    value: number;
    total: number;
    count: number;
  };
  photos: PhotoHash[];
  totals: {
    reviews: number;
    photos: number;
    trails: number;
  };
  staticMap: {
    exploreLink: string;
  };
  rivers: River[];
  orderedBreadcrumbList: LocationBreadcrumb[];
  metadata?: {
    updated_at: string;
  };
};

// Only for Parks
type ParkPageDetailsApiResponse = {
  printLink?: string;
  parkInfo?: ParkInfoType;
  parkInfoDescriptionHtml?: string;
  parkAlertBanner?: ParkSection | null;
  parkPlanYourVisit?: ParkSection | null;
};

// Only for POIs
type PoiPageDetailsApiResponse = {
  associatedLocationSlug?: string;
  primaryPark?: {
    name: string;
    slug: string;
  };
  mentionReviewSnippets?: Review[];
  easiestTrailsToPoi?: EasiestTrailsToGetToPoiType | null;
  poiType?: POISubType;
};

export type LocationPageDetailsApiResponse = ParkPageDetailsApiResponse &
  PoiPageDetailsApiResponse & {
    locationType: LocationDetailsResponseType;
    directionsLink: string;
    description: string;
    metaDescription: string;
    filterConfig: FilterConfig;
    initialTrailResults: AlgoliaTrail[];
    shareOgImage: {
      url: string | null;
      url_https?: string;
      width?: number;
      height?: number;
    };
    h2SeoTest: string | null;
    localizedLocationSlug: string;
    seoTestCanonicalUrl: string | null;
    pageFilterUid?: PageFilterType | null;
    seoTestFeatures: string[];
    title: string;
    resultsTitle: string;
    overview: string;
    visitorStats?: VisitorStats;
    bestEasyTrails?: AlgoliaTrail[];
    seoKeywordReviews?: Review[];
  };

type PoiSeoDetailsApiResponse = {
  parentLocation?: {
    type: 'city' | 'state' | 'country';
    name: string;
    slug: string;
    tags: Tag[];
  };
};

export type LocationSeoDetailsApiResponse = PoiSeoDetailsApiResponse & {
  guides: GuideHit[];
  reviewSnippets: ReviewAndTrail[];
  faqData: FaqDataType | null;
  tags: Tag[];
  seoLinks?:
    | {
        key: SeoLinksSectionKey;
        data: SeoLinks;
      }[]
    | null;
  pointsOfInterest: PointOfInterest[];
};

export type LocationDetailsApiResponse = LocationBasePageApiResponse & LocationPageDetailsApiResponse & LocationSeoDetailsApiResponse;

export type PlaceType = 'poi' | 'country' | 'state' | 'city' | 'area';

export enum ParkSectionType {
  AlertBanner = 'alert-banner',
  PlanYourVisit = 'plan-your-visit'
}

export enum ParkSectionRel {
  HoursContact = 'park-hours-contact',
  PassesFees = 'park-passes-fees',
  Accessibility = 'park-accessibility'
}

export type ParkSectionLink = {
  rel: ParkSectionRel;
  title: string;
  href: string;
  description?: string;
};

export type ParkSection = {
  sectionType: ParkSectionType;
  priorityType?: string;
  alertBanner?: { bannerType: LocationPageAlertType; heading: string; subheading?: string };
  sectionTitle?: string;
  links?: ParkSectionLink[];
};

export type ParkSectionsApiResponse = {
  sections: ParkSection[];
};

export enum SectionType {
  HoursSummary = 'hours-summary',
  Divider = 'divider',
  Contact = 'contact',
  HoursWeekly = 'hours-weekly',
  PassFee = 'pass-fee',
  SectionHeader = 'section-header',
  TextBlock = 'text-block',
  TextBlockExpandable = 'text-block-expandable'
}

export enum VisitorInfoRel {
  ContactPhone = 'contact-phone',
  External = 'external',
  ParkAccessibility = 'park-accessibility',
  ParkAlerts = 'park-alerts',
  ParkHoursContact = 'park-hours-contact',
  ParkPassesFees = 'park-passes-fees',
  Alerts = 'alerts'
}

export type VisitorInfoLink = {
  title: string;
  description?: string;
  href: string;
  rel: VisitorInfoRel;
};

export type VisitorInfoSection = {
  title?: string;
  description?: string;
  sectionType: SectionType;
  links?: VisitorInfoLink[];
  cost?: string;
  padding?: boolean;
  dayHours?: {
    day: string;
    schedule: string;
  }[];
};

export type VisitorInfoModalResponse = {
  sections: VisitorInfoSection[];
};

export type ParkAlert = {
  id: number;
  title: string;
  description: string;
};

export type ParkAlertSection = {
  sectionType: 'caution' | 'information' | 'closure' | 'danger';
  alerts: ParkAlert[];
};

export type ParkAlertsApiResponse = {
  sections: ParkAlertSection[];
  links: {
    rel: string;
    href: string;
  }[];
};
